import { uiText } from '../i18n/uiText';
import React, { createContext, useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, MessageSquare, X } from 'lucide-react';
import Dialog from '../components/common/Dialog';

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [dialog, setDialog] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const resolverRef = useRef(null);
  const inputRef = useRef(null);
  const primaryRef = useRef(null);
  const titleId = useId();
  const messageId = useId();

  const openDialog = useCallback((mode, message, options = {}) => new Promise((resolve) => {
    resolverRef.current?.(mode === 'prompt' ? null : false);
    resolverRef.current = resolve;
    setInputValue(String(options.defaultValue ?? ''));
    setDialog({
      mode,
      title: uiText(options.title || (mode === 'prompt' ? 'Введите данные' : 'Подтвердите действие')),
      message: uiText(message),
      confirmLabel: uiText(options.confirmLabel || (mode === 'prompt' ? 'Продолжить' : 'Подтвердить')),
      cancelLabel: uiText(options.cancelLabel || 'Отмена'),
      placeholder: options.placeholder || '',
      required: Boolean(options.required),
      inputType: options.inputType || 'text',
      danger: mode === 'confirm' && options.danger !== false,
    });
  }), []);

  const confirm = useCallback((message, options = {}) => openDialog('confirm', message, options), [openDialog]);
  const prompt = useCallback((message, options = {}) => openDialog('prompt', message, options), [openDialog]);

  const finish = useCallback((result) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setDialog(null);
  }, []);

  useEffect(() => () => {
    resolverRef.current?.(null);
    resolverRef.current = null;
  }, []);

  const submit = (event) => {
    event.preventDefault();
    if (dialog.mode === 'prompt') {
      if (dialog.required && !inputValue.trim()) {
        inputRef.current?.focus();
        return;
      }
      finish(inputValue);
      return;
    }
    finish(true);
  };

  return (
    <ConfirmContext.Provider value={{ confirm, prompt }}>
      {children}
      {dialog ? (
        <Dialog size="sm" labelledBy={titleId} describedBy={messageId}
          role={dialog.mode === 'confirm' && dialog.danger ? 'alertdialog' : 'dialog'}
          initialFocusRef={dialog.mode === 'prompt' ? inputRef : undefined}
          onClose={() => finish(dialog.mode === 'prompt' ? null : false)}>
          <form
            onSubmit={submit}
          >
            <div className="ap-dialog__header">
              <div className="app-confirm-heading">
                <span className={`app-confirm-icon ${dialog.danger ? 'is-danger' : ''}`} aria-hidden="true">
                  {dialog.mode === 'prompt' ? <MessageSquare size={19} /> : <AlertTriangle size={19} />}
                </span>
                <h2 className="ap-dialog__title" id={titleId} data-dialog-heading tabIndex={-1}>{dialog.title}</h2>
              </div>
              <button type="button" className="ap-dialog__close" aria-label={uiText("Закрыть")} onClick={() => finish(dialog.mode === 'prompt' ? null : false)}><X size={18} /></button>
            </div>
            <div className="ap-dialog__body">
              <p id={messageId} className="app-confirm-message">{dialog.message}</p>
              {dialog.mode === 'prompt' ? (
                <label className="app-prompt-field">
                  <span className="sr-only">{dialog.message}</span>
                  <input
                    ref={inputRef}
                    className="ap-control"
                    type={dialog.inputType}
                    value={inputValue}
                    placeholder={dialog.placeholder}
                    required={dialog.required}
                    onChange={(event) => setInputValue(event.target.value)}
                  />
                </label>
              ) : null}
            </div>
            <div className="ap-dialog__footer">
              <button type="button" className="ap-button ap-button--secondary" onClick={() => finish(dialog.mode === 'prompt' ? null : false)}>{dialog.cancelLabel}</button>
              <button ref={primaryRef} type="submit" className={dialog.danger ? 'ap-button ap-button--danger' : 'ap-button ap-button--primary'}>{dialog.confirmLabel}</button>
            </div>
          </form>
        </Dialog>
      ) : null}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('useConfirm must be used inside ConfirmProvider');
  return context.confirm;
}

export function usePrompt() {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error('usePrompt must be used inside ConfirmProvider');
  return context.prompt;
}
